import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notificationsApi } from '../store/api/notificationsApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Reconnect backoff. Capped so a long backend outage doesn't leave a tab
// permanently disconnected, and jittered so every open tab doesn't retry in
// lockstep after a deploy.
const BASE_RETRY_MS = 2000;
const MAX_RETRY_MS = 30000;

// While the stream is down, fall back to polling so the badge still moves.
const FALLBACK_POLL_MS = 60000;

const retryDelay = (attempt) => {
  const capped = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
  return capped / 2 + Math.random() * (capped / 2);
};

/**
 * Keeps the notification cache live over Server-Sent Events.
 *
 * Mount once (the header does it). The hook opens one EventSource for the
 * logged-in merchant, writes incoming events straight into the
 * `getNotifications` RTK Query cache, and reconnects with backoff on drop.
 *
 * @param {object} options
 * @param {(notification: object) => void} [options.onNotification]
 *   Called for each newly arrived notification — used for the toast.
 */
const useNotificationStream = ({ onNotification } = {}) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  // Scalar id, not the user object, so a re-created object never reopens the stream.
  const userId = useSelector((state) => state.auth.user?.id ?? null);

  // Held in refs so reconnect logic never re-triggers the effect.
  const sourceRef = useRef(null);
  const retryTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);
  const onNotificationRef = useRef(onNotification);

  // Keep the callback current without re-opening the stream on every render.
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!token || !userId) return undefined;

    closedRef.current = false;

    const patchFeed = (recipe) =>
      dispatch(notificationsApi.util.updateQueryData('getNotifications', undefined, recipe));

    const clearTimers = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    // Refetching the feed is the safe way to resync: it picks up anything that
    // happened while the socket was down, including rows from other tabs.
    const resync = () => {
      dispatch(notificationsApi.util.invalidateTags(['Notifications']));
    };

    const startFallbackPolling = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(resync, FALLBACK_POLL_MS);
    };

    const stopFallbackPolling = () => {
      if (!pollTimerRef.current) return;
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };

    const scheduleReconnect = () => {
      if (closedRef.current) return;
      startFallbackPolling();
      const delay = retryDelay(attemptRef.current);
      attemptRef.current += 1;
      retryTimerRef.current = setTimeout(connect, delay);
    };

    async function connect() {
      if (closedRef.current) return;

      // Close any half-open source before replacing it.
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }

      let ticket;
      try {
        // track: false keeps the one-shot ticket out of the mutation cache.
        const result = await dispatch(
          notificationsApi.endpoints.createStreamTicket.initiate(undefined, { track: false })
        ).unwrap();
        ticket = result?.ticket;
      } catch (error) {
        // A 401 is already handled by the base query (logout + redirect). Any
        // other failure is transient — back off and try again.
        scheduleReconnect();
        return;
      }

      if (!ticket) {
        scheduleReconnect();
        return;
      }

      const source = new EventSource(
        `${API_BASE_URL}/notifications/stream?ticket=${encodeURIComponent(ticket)}`
      );
      sourceRef.current = source;

      source.addEventListener('connected', () => {
        attemptRef.current = 0;
        stopFallbackPolling();
        // Catch up on anything missed while disconnected.
        resync();
      });

      source.addEventListener('notification', (event) => {
        let parsed;
        try {
          parsed = JSON.parse(event.data);
        } catch (error) {
          return;
        }

        const raw = parsed?.notification;
        if (!raw) return;

        // The stream sends the DB row (snake_case); map it to the shape the
        // REST feed returns so both sources render identically.
        const notification = {
          id: raw.id,
          eventType: raw.event_type,
          title: raw.title,
          body: raw.body,
          severity: raw.severity,
          link: raw.link,
          metadata: raw.metadata || null,
          read: Boolean(raw.read_at),
          readAt: raw.read_at || null,
          createdAt: raw.created_at,
        };

        patchFeed((draft) => {
          // Guard against a duplicate arriving alongside a refetch.
          if (draft.items.some((n) => n.id === notification.id)) return;
          draft.items.unshift(notification);
          draft.unreadCount =
            typeof parsed.unreadCount === 'number'
              ? parsed.unreadCount
              : draft.unreadCount + 1;
        });

        if (onNotificationRef.current) onNotificationRef.current(notification);
      });

      // Another tab marked one read.
      source.addEventListener('read', (event) => {
        try {
          const { id, unreadCount } = JSON.parse(event.data);
          patchFeed((draft) => {
            const row = draft.items.find((n) => n.id === id);
            if (row) {
              row.read = true;
              row.readAt = new Date().toISOString();
            }
            if (typeof unreadCount === 'number') draft.unreadCount = unreadCount;
          });
        } catch (error) {
          /* ignore malformed frame */
        }
      });

      // Another tab put one back to unread.
      source.addEventListener('unread', (event) => {
        try {
          const { id, unreadCount } = JSON.parse(event.data);
          patchFeed((draft) => {
            const row = draft.items.find((n) => n.id === id);
            if (row) {
              row.read = false;
              row.readAt = null;
            }
            if (typeof unreadCount === 'number') draft.unreadCount = unreadCount;
          });
        } catch (error) {
          /* ignore malformed frame */
        }
      });

      source.addEventListener('read-all', () => {
        patchFeed((draft) => {
          const now = new Date().toISOString();
          draft.items.forEach((n) => {
            if (!n.read) {
              n.read = true;
              n.readAt = now;
            }
          });
          draft.unreadCount = 0;
        });
      });

      source.addEventListener('removed', (event) => {
        try {
          const { id, unreadCount } = JSON.parse(event.data);
          patchFeed((draft) => {
            const idx = draft.items.findIndex((n) => n.id === id);
            if (idx !== -1) draft.items.splice(idx, 1);
            if (typeof unreadCount === 'number') draft.unreadCount = unreadCount;
          });
        } catch (error) {
          /* ignore malformed frame */
        }
      });

      // Another tab cleared the whole feed.
      source.addEventListener('cleared', () => {
        patchFeed((draft) => {
          draft.items = [];
          draft.unreadCount = 0;
          draft.hasMore = false;
          draft.nextCursor = null;
        });
      });

      // Sent right after connect so a reconnecting tab fixes its badge even if
      // no notification arrives.
      source.addEventListener('sync', (event) => {
        try {
          const { unreadCount } = JSON.parse(event.data);
          if (typeof unreadCount === 'number') {
            patchFeed((draft) => {
              draft.unreadCount = unreadCount;
            });
          }
        } catch (error) {
          /* ignore malformed frame */
        }
      });

      source.onerror = () => {
        // The ticket is single-use, so the browser's own retry would 401.
        // Close and mint a fresh one instead.
        source.close();
        if (sourceRef.current === source) sourceRef.current = null;
        scheduleReconnect();
      };
    }

    connect();

    // A backgrounded tab can have its socket reaped without an error firing.
    // Re-check on focus so the bell is never silently stale.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resync();
      if (!sourceRef.current) {
        attemptRef.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearTimers();
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
    };
  }, [dispatch, token, userId]);
};

export default useNotificationStream;
